import React, { memo, useEffect, useMemo, useRef, useState } from '../../../../lib/teact/teact';
import { getActions, withGlobal } from '../../../../global';

import type {
  ApiBaseCurrency, ApiCurrencyRates, ApiWalletWithVersionInfo,
} from '../../../../api/types';
import type { Account, AccountSettings, GlobalState } from '../../../../global/types';
import { AccountSelectorState } from '../../../../global/types';
import { SettingsState } from '../../../../global/types';

import {
  selectCurrentAccountId,
  selectIsMnemonicAccount,
  selectIsPasswordPresent,
  selectNetworkAccounts,
  selectOrderedAccounts,
} from '../../../../global/selectors';
import { getHasInMemoryPassword, getInMemoryPassword } from '../../../../util/authApi/inMemoryPasswordStore';
import buildClassName from '../../../../util/buildClassName';
import { captureEvents, SwipeDirection } from '../../../../util/captureEvents';
import { getChainsSupportingLedger } from '../../../../util/chain';
import { vibrate } from '../../../../util/haptics';
import { disableSwipeToClose, enableSwipeToClose } from '../../../../util/modalSwipeManager';
import { isVaultUnlocked, unlockVaultAccount } from '../../../../util/vaultUnlock';
import { IS_LEDGER_SUPPORTED, IS_TOUCH_ENV } from '../../../../util/windowEnvironment';
import { callApi } from '../../../../api';
import { buildTabs, getCurrentTabIndex } from './helpers/tabsHelper';
import { AccountTab, DEFAULT_TAB, OPEN_CONTEXT_MENU_CLASS_NAME } from './constants';

import useEffectOnce from '../../../../hooks/useEffectOnce';
import useFlag from '../../../../hooks/useFlag';
import useLang from '../../../../hooks/useLang';
import useLastCallback from '../../../../hooks/useLastCallback';
import { useMultipleAccountsBalances } from '../../../../hooks/useMultipleAccountsBalances';
import useScrolledState from '../../../../hooks/useScrolledState';
import useSyncEffect from '../../../../hooks/useSyncEffect';
import { useFilteredAccounts } from './hooks/useFilteredAccounts';
import { useSortableAccounts } from './hooks/useSortableAccounts';

import AuthImportViewAccount from '../../../auth/AuthImportViewAccount';
import LedgerConnect from '../../../ledger/LedgerConnect';
import LedgerSelectWallets from '../../../ledger/LedgerSelectWallets';
import Modal from '../../../ui/Modal';
import TabList from '../../../ui/TabList';
import Transition from '../../../ui/Transition';
import LogOutModal from '../LogOutModal';
import AccountSelectorFooter from './AccountSelectorFooter';
import AccountSelectorHeader from './AccountSelectorHeader';
import AccountsGridView from './AccountsGridView';
import AccountsListView from './AccountsListView';
import AddAccountPasswordModal from './AddAccountPasswordModal';
import AddAccountSelector from './AddAccountSelector';

import modalStyles from '../../../ui/Modal.module.scss';
import styles from './AccountSelectorModal.module.scss';

interface AccountSelectorOpenProps {
  isOpen: true;
  currentAccountId: string;
  baseCurrency: ApiBaseCurrency;
  currencyRates: ApiCurrencyRates;
  orderedAccounts: Array<[string, Account]>;
  networkAccounts?: Record<string, Account>;
  byAccountId: GlobalState['byAccountId'];
  tokenInfo: GlobalState['tokenInfo'];
  settingsByAccountId: Record<string, AccountSettings>;
  activeTab?: number;
  viewModeInitial?: 'cards' | 'list';
  areTokensWithNoCostHidden?: boolean;
  isSensitiveDataHidden?: true;
  isTestnet?: boolean;
  isLoading?: boolean;
  error?: string;
  isPasswordPresent: boolean;
  accountWalletVersions?: ApiWalletWithVersionInfo[];
  canAddSubwallet: boolean;
  forceAddingTonOnlyAccount?: boolean;
  pendingAccountProfile?: Account['profile'];
  initialAuthState?: AccountSelectorState;
  shouldHideAddAccountBackButton?: boolean;
}

type StateProps = AccountSelectorOpenProps
  | ({ isOpen: false } & Partial<Omit<AccountSelectorOpenProps, 'isOpen'>>);

function AccountSelectorModal({
  isOpen,
  currentAccountId = '',
  baseCurrency,
  currencyRates,
  orderedAccounts,
  networkAccounts,
  byAccountId,
  tokenInfo,
  settingsByAccountId,
  activeTab = DEFAULT_TAB,
  viewModeInitial,
  areTokensWithNoCostHidden,
  isSensitiveDataHidden,
  isTestnet,
  isLoading,
  error,
  isPasswordPresent = false,
  accountWalletVersions,
  canAddSubwallet = false,
  forceAddingTonOnlyAccount,
  pendingAccountProfile,
  initialAuthState,
  shouldHideAddAccountBackButton,
}: StateProps) {
  const {
    closeAccountSelector,
    switchAccount,
    setAccountSelectorTab,
    setAccountSelectorViewMode,
    rebuildOrderedAccountIds,
    addAccount,
    createSubWallet,
    clearAccountError,
    openSettingsWithState,
    resetHardwareWalletConnect,
    clearAccountLoading,
    openWalletRenameModal,
    setPendingAccountProfile,
  } = getActions();

  const lang = useLang();
  const contentRef = useRef<HTMLDivElement>();

  const initialRenderingKey = viewModeInitial === 'list'
    ? AccountSelectorState.List
    : AccountSelectorState.Cards;
  const [renderingKey, setRenderingKey] = useState<AccountSelectorState>(initialRenderingKey);
  const skipAnimationToKeyRef = useRef<AccountSelectorState | undefined>();
  const [isLogOutModalOpen, openLogOutModal, closeLogOutModal] = useFlag(false);
  const [logOutAccountId, setLogOutAccountId] = useState<string | undefined>();
  const [isNewAccountImporting, setIsNewAccountImporting] = useState<boolean>(false);
  const [isAddingSubwallet, setIsAddingSubwallet] = useState<boolean>(false);
  const [pendingVaultUnlockAccountId, setPendingVaultUnlockAccountId] = useState<string | undefined>();
  const [vaultUnlockError, setVaultUnlockError] = useState<string | undefined>();
  const [isVaultUnlockLoading, setIsVaultUnlockLoading] = useState(false);
  const [previousViewMode, setPreviousViewMode] = useState<AccountSelectorState>(initialRenderingKey);
  const [shouldReturnToStartScreen, setShouldReturnToStartScreen] = useState<boolean>(false);

  const hasOtherWalletVersions = useMemo(() => (
    (accountWalletVersions?.filter((v) => v.lastTxId || v.version === 'W5').length ?? 0) > 1
  ), [accountWalletVersions]);

  const tabs = useMemo(() => buildTabs(isTestnet ?? false, lang), [isTestnet, lang]);
  const currentTabIndex = useMemo(() => getCurrentTabIndex(tabs, activeTab), [activeTab, tabs]);
  const selectedTab = tabs[currentTabIndex]?.id ?? DEFAULT_TAB;
  const filteredAccounts = useFilteredAccounts(orderedAccounts, selectedTab);
  const { balancesByAccountId, totalBalance, displayedAccounts } = useMultipleAccountsBalances({
    filteredAccounts,
    sourceAccounts: networkAccounts,
    byAccountId,
    tokenInfo,
    settingsByAccountId,
    areTokensWithNoCostHidden,
    baseCurrency,
    currencyRates,
  });
  const { sortState, handleDrag, handleDragEnd } = useSortableAccounts(filteredAccounts);

  const {
    isScrolled,
    isAtEnd: noButtonsSeparator,
    update: handleScrollInitialize,
    handleScroll,
  } = useScrolledState();

  useEffectOnce(rebuildOrderedAccountIds);

  useEffect(() => {
    if (!isOpen) return undefined;

    disableSwipeToClose();

    return enableSwipeToClose;
  }, [isOpen]);

  useSyncEffect(() => {
    if (!isOpen || forceAddingTonOnlyAccount || initialAuthState === undefined) {
      skipAnimationToKeyRef.current = undefined;
      return;
    }

    const state = initialAuthState;
    let targetKey: AccountSelectorState;
    if (state === AccountSelectorState.AddAccountConnectHardware && IS_LEDGER_SUPPORTED && !isTestnet) {
      targetKey = AccountSelectorState.AddAccountConnectHardware;
    } else if (state === AccountSelectorState.AddAccountViewMode
      || state === AccountSelectorState.AddAccountInitial) {
      targetKey = state;
    } else {
      targetKey = initialRenderingKey;
    }

    skipAnimationToKeyRef.current = targetKey;
    setRenderingKey(targetKey);
  }, [isOpen, forceAddingTonOnlyAccount, initialAuthState, initialRenderingKey, isTestnet]);

  useEffect(() => {
    if (!isOpen) return;

    if (forceAddingTonOnlyAccount) {
      if (pendingAccountProfile === 'vault') {
        handleNewVaultAccountClick();
      } else {
        handleNewAccountClick();
      }
      return;
    }

    if (initialAuthState === AccountSelectorState.AddAccountConnectHardware && IS_LEDGER_SUPPORTED && !isTestnet) {
      handleImportHardwareWalletClick();
    }
  }, [isOpen, forceAddingTonOnlyAccount, pendingAccountProfile, initialAuthState, isTestnet]);

  useEffect(() => {
    if (!IS_TOUCH_ENV || !isOpen) return;

    const node = contentRef.current;
    if (!node) return;

    function handleSwipe(e: Event, direction: SwipeDirection) {
      if (
        direction === SwipeDirection.Up
        || direction === SwipeDirection.Down
        || renderingKey === AccountSelectorState.Reorder
        || (e.target as HTMLElement | null)?.closest(`.${OPEN_CONTEXT_MENU_CLASS_NAME}`)

      ) {
        return false;
      }

      const nextIndex = direction === SwipeDirection.Left
        ? Math.min(tabs.length - 1, currentTabIndex + 1)
        : Math.max(0, currentTabIndex - 1);
      if (nextIndex === currentTabIndex) return false;

      const nextTab = tabs[nextIndex];
      handleSwitchTab(nextTab.id);
      return true;
    }

    return captureEvents(node, {
      includedClosestSelector: '.swipe-container',
      selectorToPreventScroll: '.custom-scroll',
      onSwipe: handleSwipe,
    });
  }, [currentTabIndex, isOpen, renderingKey, tabs]);

  const handleCloseAccountSelectorForced = useLastCallback(() => {
    closeAccountSelector(undefined, { forceOnHeavyAnimation: true });
  });

  const handleModalClose = useLastCallback(() => {
    setRenderingKey(initialRenderingKey);
    setIsNewAccountImporting(false);
    setIsAddingSubwallet(false);
    setShouldReturnToStartScreen(false);
    setPreviousViewMode(initialRenderingKey);
    setPendingVaultUnlockAccountId(undefined);
    setVaultUnlockError(undefined);
    setIsVaultUnlockLoading(false);
    clearAccountLoading();
  });

  const handleBackFromAddAccount = useLastCallback(() => {
    switch (renderingKey) {
      case AccountSelectorState.AddAccountPassword:
        setRenderingKey(AccountSelectorState.AddAccountInitial);
        setIsAddingSubwallet(false);
        clearAccountError();
        break;

      case AccountSelectorState.UnlockVault:
        setPendingVaultUnlockAccountId(undefined);
        setVaultUnlockError(undefined);
        setIsVaultUnlockLoading(false);
        setRenderingKey(previousViewMode);
        break;

      case AccountSelectorState.AddAccountViewMode:
      case AccountSelectorState.AddAccountConnectHardware:
        if (shouldReturnToStartScreen) {
          setRenderingKey(previousViewMode);
          setShouldReturnToStartScreen(false);
        } else {
          setRenderingKey(AccountSelectorState.AddAccountInitial);
        }
        break;

      case AccountSelectorState.AddAccountSelectHardware:
        setRenderingKey(AccountSelectorState.AddAccountConnectHardware);
        break;

      default:
        setRenderingKey(previousViewMode);
    }
  });

  const completeSwitchAccount = useLastCallback((accountId: string) => {
    vibrate();
    handleCloseAccountSelectorForced();

    if (accountId !== currentAccountId) {
      switchAccount({ accountId });
    }
  });

  const handleSwitchAccount = useLastCallback((accountId: string) => {
    if (accountId === currentAccountId) {
      handleCloseAccountSelectorForced();
      return;
    }

    const account = networkAccounts?.[accountId]
      ?? orderedAccounts?.find(([id]) => id === accountId)?.[1];
    if (account?.profile === 'vault' && !isVaultUnlocked(accountId)) {
      if (getHasInMemoryPassword()) {
        void getInMemoryPassword().then(async (password) => {
          if (password && await callApi('verifyPassword', password)) {
            unlockVaultAccount(accountId);
            completeSwitchAccount(accountId);
            return;
          }

          setPreviousViewMode(renderingKey);
          setPendingVaultUnlockAccountId(accountId);
          setVaultUnlockError(undefined);
          setRenderingKey(AccountSelectorState.UnlockVault);
        });
        return;
      }

      setPreviousViewMode(renderingKey);
      setPendingVaultUnlockAccountId(accountId);
      setVaultUnlockError(undefined);
      setRenderingKey(AccountSelectorState.UnlockVault);
      return;
    }

    completeSwitchAccount(accountId);
  });

  const handleUnlockVaultSubmit = useLastCallback(async (password: string) => {
    if (!pendingVaultUnlockAccountId) return;

    setIsVaultUnlockLoading(true);
    setVaultUnlockError(undefined);

    const isValid = await callApi('verifyPassword', password);
    if (!isValid) {
      setIsVaultUnlockLoading(false);
      setVaultUnlockError(lang('Wrong password, please try again.'));
      return;
    }

    unlockVaultAccount(pendingVaultUnlockAccountId);
    const accountId = pendingVaultUnlockAccountId;
    setPendingVaultUnlockAccountId(undefined);
    setIsVaultUnlockLoading(false);
    completeSwitchAccount(accountId);
  });

  const handleAddAccountAction = useLastCallback((method: 'createAccount' | 'importMnemonic') => {
    if (!isPasswordPresent) {
      addAccount({ method, password: '' });
      return;
    }

    if (getHasInMemoryPassword()) {
      void getInMemoryPassword()
        .then((password) => addAccount({
          method,
          password: password!,
        }));
      return;
    }

    setIsNewAccountImporting(method === 'importMnemonic');
    setRenderingKey(AccountSelectorState.AddAccountPassword);
  });

  const handleNewAccountClick = useLastCallback(() => {
    setPendingAccountProfile({ profile: 'daily' });
    handleAddAccountAction('createAccount');
  });

  const handleNewVaultAccountClick = useLastCallback(() => {
    setPendingAccountProfile({ profile: 'vault' });
    handleAddAccountAction('createAccount');
  });

  const handleImportAccountClick = useLastCallback(() => {
    setPendingAccountProfile({ profile: 'daily' });
    handleAddAccountAction('importMnemonic');
  });

  const handleImportHardwareWalletClick = useLastCallback(() => {
    resetHardwareWalletConnect({
      chain: getChainsSupportingLedger()[0],
      shouldLoadWallets: true,
    });
    setRenderingKey(AccountSelectorState.AddAccountConnectHardware);
  });

  const handleViewModeWalletClick = useLastCallback(() => {
    setShouldReturnToStartScreen(false);
    setRenderingKey(AccountSelectorState.AddAccountViewMode);
  });

  const handleSubmitPassword = useLastCallback((password: string) => {
    if (isAddingSubwallet) {
      createSubWallet({ password });
      handleCloseAccountSelectorForced();
      return;
    }

    addAccount({ method: isNewAccountImporting ? 'importMnemonic' : 'createAccount', password });
  });

  const handleHardwareWalletConnected = useLastCallback(() => {
    setRenderingKey(AccountSelectorState.AddAccountSelectHardware);
  });

  const handleViewModeChange = useLastCallback((state: AccountSelectorState) => {
    setRenderingKey(state);

    if (state === AccountSelectorState.List || state === AccountSelectorState.Cards) {
      setAccountSelectorViewMode({ mode: state === AccountSelectorState.List ? 'list' : 'cards' });
    }
  });

  const handleSwitchTab = useLastCallback((tabId: number) => {
    if (renderingKey === AccountSelectorState.Reorder) return;

    setAccountSelectorTab({ tab: tabId });
  });

  const handleAddWalletClick = useLastCallback(() => {
    vibrate();
    setPreviousViewMode(renderingKey);

    const selectedTabId = tabs[currentTabIndex]?.id ?? AccountTab.My;
    if (selectedTabId === AccountTab.View) {
      setShouldReturnToStartScreen(true);
      setRenderingKey(AccountSelectorState.AddAccountViewMode);
    } else if (selectedTabId === AccountTab.Ledger) {
      setShouldReturnToStartScreen(true);
      handleImportHardwareWalletClick();
    } else {
      setShouldReturnToStartScreen(false);
      setRenderingKey(AccountSelectorState.AddAccountInitial);
    }
  });

  const handleReorderClick = useLastCallback(() => {
    setRenderingKey(AccountSelectorState.Reorder);
    setAccountSelectorTab({ tab: AccountTab.All });
  });

  const handleReorderDoneClick = useLastCallback(() => {
    vibrate();
    const previousMode = viewModeInitial === 'list'
      ? AccountSelectorState.List
      : AccountSelectorState.Cards;
    setRenderingKey(previousMode);
  });

  const handleRenameClick = useLastCallback((accountId: string) => {
    vibrate();
    openWalletRenameModal({ accountId });
  });

  const handleLogOutClick = useLastCallback((accountId: string) => {
    vibrate();
    setLogOutAccountId(accountId);
    openLogOutModal();
  });

  const handleLogOutModalClose = useLastCallback(() => {
    closeLogOutModal();
    setLogOutAccountId(undefined);

    if (filteredAccounts.length === 0 || currentAccountId === logOutAccountId) {
      handleCloseAccountSelectorForced();
    }
  });

  const handleOpenSettingWalletVersion = useLastCallback(() => {
    handleCloseAccountSelectorForced();
    openSettingsWithState({ state: SettingsState.WalletVersions });
  });

  const handleNewSubwalletClick = useLastCallback(() => {
    if (getHasInMemoryPassword()) {
      void getInMemoryPassword().then((password) => {
        createSubWallet({ password: password! });
        handleCloseAccountSelectorForced();
      });
      return;
    }

    setIsAddingSubwallet(true);
    setRenderingKey(AccountSelectorState.AddAccountPassword);
  });

  function renderHeader(renderingState: AccountSelectorState) {
    const isInactiveTabs = renderingKey === AccountSelectorState.Reorder;

    return (
      <div className={buildClassName(styles.headerWrapper, isScrolled && styles.withBorder)}>
        <AccountSelectorHeader
          walletsCount={filteredAccounts.length}
          totalBalance={totalBalance}
          renderingState={renderingState}
          isSensitiveDataHidden={isSensitiveDataHidden}
          onViewModeChange={handleViewModeChange}
          onReorderClick={handleReorderClick}
        />

        <div className={styles.tabsContainer}>
          <TabList
            tabs={tabs}
            activeTab={currentTabIndex}
            className={buildClassName(styles.tabs, isInactiveTabs && styles.inactive)}
            onSwitchTab={handleSwitchTab}
          />
        </div>
      </div>
    );
  }

  function renderFooter(renderingState: AccountSelectorState, selectedTab: AccountTab) {
    return (
      <AccountSelectorFooter
        tab={selectedTab}
        renderingState={renderingState}
        withBorder={!noButtonsSeparator}
        onAddWallet={handleAddWalletClick}
        onReorderDone={handleReorderDoneClick}
      />
    );
  }

  function renderContent(isActive: boolean, isFrom: boolean, currentKey: AccountSelectorState) {
    const commonAccountsViewProps = {
      isActive,
      isTestnet,
      filteredAccounts: displayedAccounts ?? filteredAccounts,
      activeTab: selectedTab,
      balancesByAccountId,
      settingsByAccountId,
      currentAccountId,
      isSensitiveDataHidden,
      onScrollInitialize: handleScrollInitialize,
      onScroll: handleScroll,
      onSwitchAccount: handleSwitchAccount,
      onRename: handleRenameClick,
      onReorder: handleReorderClick,
      onLogOut: handleLogOutClick,
    };

    switch (currentKey) {
      case AccountSelectorState.Cards:
        return (
          <>
            {renderHeader(AccountSelectorState.Cards)}
            <AccountsGridView {...commonAccountsViewProps} />
            {renderFooter(AccountSelectorState.Cards, selectedTab)}
          </>
        );

      case AccountSelectorState.List:
        return (
          <>
            {renderHeader(AccountSelectorState.List)}
            <AccountsListView {...commonAccountsViewProps} />
            {renderFooter(AccountSelectorState.List, selectedTab)}
          </>
        );

      case AccountSelectorState.Reorder:
        return (
          <>
            {renderHeader(AccountSelectorState.Reorder)}
            <AccountsListView
              {...commonAccountsViewProps}
              isReorder
              sortState={sortState}
              onDrag={handleDrag}
              onDragEnd={handleDragEnd}
            />
            {renderFooter(AccountSelectorState.Reorder, selectedTab)}
          </>
        );

      case AccountSelectorState.AddAccountInitial:
        return (
          <AddAccountSelector
            isNewAccountImporting={isNewAccountImporting}
            isLoading={isLoading}
            isTestnet={isTestnet}
            hasOtherWalletVersions={hasOtherWalletVersions}
            canAddSubwallet={canAddSubwallet}
            shouldHideBackButton={shouldHideAddAccountBackButton}
            onBack={handleBackFromAddAccount}
            onNewAccountClick={handleNewAccountClick}
            onNewVaultAccountClick={handleNewVaultAccountClick}
            onNewSubwalletClick={handleNewSubwalletClick}
            onImportAccountClick={handleImportAccountClick}
            onImportHardwareWalletClick={handleImportHardwareWalletClick}
            onViewModeWalletClick={handleViewModeWalletClick}
            onOpenSettingWalletVersion={handleOpenSettingWalletVersion}
            onClose={handleCloseAccountSelectorForced}
          />
        );

      case AccountSelectorState.AddAccountPassword:
        return (
          <AddAccountPasswordModal
            isActive={isActive}
            isLoading={isLoading}
            error={error}
            onClearError={clearAccountError}
            onSubmit={handleSubmitPassword}
            onBack={handleBackFromAddAccount}
            onClose={handleCloseAccountSelectorForced}
          />
        );

      case AccountSelectorState.UnlockVault:
        return (
          <AddAccountPasswordModal
            isActive={isActive}
            isLoading={isVaultUnlockLoading}
            error={vaultUnlockError}
            onClearError={() => setVaultUnlockError(undefined)}
            onSubmit={handleUnlockVaultSubmit}
            onBack={handleBackFromAddAccount}
            onClose={handleCloseAccountSelectorForced}
          />
        );

      case AccountSelectorState.AddAccountConnectHardware:
        return (
          <div className={modalStyles.transitionContentWrapper}>
            <LedgerConnect
              isActive={isActive}
              onConnected={handleHardwareWalletConnected}
              onBackClick={handleBackFromAddAccount}
              onClose={handleCloseAccountSelectorForced}
            />
          </div>
        );

      case AccountSelectorState.AddAccountSelectHardware:
        return (
          <div className={modalStyles.transitionContentWrapper}>
            <LedgerSelectWallets
              withCloseButton
              onBackClick={handleBackFromAddAccount}
              onClose={handleCloseAccountSelectorForced}
            />
          </div>
        );

      case AccountSelectorState.AddAccountViewMode:
        return (
          <div className={modalStyles.transitionContentWrapper}>
            <AuthImportViewAccount
              isActive={isActive}
              isLoading={isLoading}
              isInModal
              onCancel={handleBackFromAddAccount}
              onClose={handleCloseAccountSelectorForced}
            />
          </div>
        );
    }
  }

  // When opened with `initialAuthState`, Transition sees a key change (old → target) and animates.
  // We pass `name="none"` to make this switch instant, then clear the flag once `renderingKey`
  // catches up to the target so that subsequent navigations animate normally.
  const shouldSkipTransition = skipAnimationToKeyRef.current !== undefined;
  if (skipAnimationToKeyRef.current === renderingKey) {
    skipAnimationToKeyRef.current = undefined;
  }

  return (
    <>
      <Modal
        hasCloseButton
        isOpen={isOpen}
        dialogClassName={styles.modalDialog}
        contentClassName={styles.modalContent}
        onCloseAnimationEnd={handleModalClose}
        onClose={handleCloseAccountSelectorForced}
      >
        <Transition
          ref={contentRef}
          name={shouldSkipTransition ? 'none' : 'semiFade'}
          className={buildClassName(
            modalStyles.transition,
            styles.rootTransition,
            IS_TOUCH_ENV && 'swipe-container',
          )}
          slideClassName={buildClassName(modalStyles.transitionSlide, styles.rootTransitionSlide)}
          activeKey={renderingKey}
        >
          {renderContent}
        </Transition>
      </Modal>

      <LogOutModal isOpen={isLogOutModalOpen} onClose={handleLogOutModalClose} targetAccountId={logOutAccountId} />
    </>
  );
}

export default memo(withGlobal(
  (global): StateProps => {
    const isOpen = global.isAccountSelectorOpen;

    if (!isOpen) {
      return { isOpen: false };
    }

    const {
      accounts,
      accountSelectorActiveTab: activeTab,
      accountSelectorViewMode: viewModeInitial,
      auth: {
        forceAddingTonOnlyAccount,
        pendingAccountProfile,
        initialAddAccountState: initialAuthState,
        shouldHideAddAccountBackButton,
      },
      byAccountId,
      currencyRates,
      settings: {
        byAccountId: settingsByAccountId,
        baseCurrency,
        isSensitiveDataHidden,
        areTokensWithNoCostHidden,
        isTestnet,
      },
      tokenInfo,
      walletVersions,
    } = global;

    const orderedAccounts = selectOrderedAccounts(global);
    const currentAccountId = selectCurrentAccountId(global)!;
    const networkAccounts = selectNetworkAccounts(global);
    const isPasswordPresent = selectIsPasswordPresent(global);
    const canAddSubwallet = selectIsMnemonicAccount(global);
    const accountWalletVersions = walletVersions?.byId?.[currentAccountId];
    const { isLoading, error } = accounts ?? {};

    return {
      isOpen,
      currentAccountId,
      orderedAccounts,
      networkAccounts,
      byAccountId,
      tokenInfo,
      settingsByAccountId,
      baseCurrency,
      currencyRates,
      areTokensWithNoCostHidden,
      isSensitiveDataHidden,
      activeTab,
      viewModeInitial,
      isTestnet,
      isLoading,
      error,
      isPasswordPresent,
      accountWalletVersions,
      canAddSubwallet,
      forceAddingTonOnlyAccount,
      pendingAccountProfile,
      initialAuthState,
      shouldHideAddAccountBackButton,
    };
  },
  (global, _, stickToFirst) => stickToFirst(selectCurrentAccountId(global)),
)(AccountSelectorModal));
